// Shared reactive UI state for Pulse (overlays, active selections, toasts).
// Mirrors the Swell store pattern (Options API friendly). Permissions are
// staff-gated only for now — Pulse has no per-member permission table yet, so
// every staff member sees all sections. A can() hook is kept so the rail and
// router can be tightened later without churn.
import { reactive } from "vue";
import { PulseService } from "@/services/pulse.service";
import { subscribeTable } from "@/services/subscribe.service";

let _tid = 0;

export const pulse = reactive({
  // -- permissions (from pulse_member; null perms = not a member = no access) --
  perms: null,
  permsLoaded: false,
  async loadPerms() {
    const { data } = await PulseService.getMyPermissions();
    this.perms = data || null;
    this.permsLoaded = true;
    return this.perms;
  },
  isAdmin() { return !!(this.perms && this.perms.is_admin); },
  // Can the current user access a given section?
  can(section) {
    const p = this.perms;
    if (!p) return false;
    if (p.is_admin) return true;
    switch (section) {
      case "email": return p.email_access !== "none";
      case "chats": return p.whatsapp_access !== "none";
      case "calls": return p.phone_access !== "none";
      case "calendar": return p.calendar_access !== "none";
      case "admin": return false;    // admin-only
      case "insights": return false; // Radar — admin-only (client sentiment, team scorecard)
      // overview, contacts: any member
      default: return true;
    }
  },

  // -- unread email count for the nav badge (kept live by the Email screen) --
  emailUnread: 0,
  setEmailUnread(n) { this.emailUnread = Math.max(0, n | 0); },
  async refreshEmailUnread() {
    const { data } = await PulseService.getEmailUnreadCount();
    this.emailUnread = data || 0;
    return this.emailUnread;
  },

  // -- unread WhatsApp chats for the nav badge (kept live by the Chats screen) --
  chatsUnread: 0,
  setChatsUnread(n) { this.chatsUnread = Math.max(0, n | 0); },
  async refreshChatsUnread() {
    const { data } = await PulseService.getChatsUnreadCount();
    this.chatsUnread = data || 0;
    return this.chatsUnread;
  },

  // -- unread Radar alerts for the bell badge --
  // Subscribed rather than polled: an escalation is the one thing worth showing
  // the moment it lands, and the analyser writes the row as the email is ingested.
  alertsUnread: 0,
  async refreshAlertsUnread() {
    const { data } = await PulseService.getNotificationUnreadCount();
    this.alertsUnread = data || 0;
    return this.alertsUnread;
  },
  // Uses the shared subscribeTable helper, which already handles duplicate mounts,
  // auth refresh and reconnect-on-timeout — all of which this would get wrong alone.
  watchAlerts() {
    if (this._alertsWatched) return;
    this._alertsWatched = true;
    subscribeTable("pulse_notification", () => { this.refreshAlertsUnread(); })
      .catch((e) => { this._alertsWatched = false; console.warn("alerts subscribe failed", e?.message || e); });
  },

  // -- global scope toggle shown across screens: "mine" vs "team" --
  scope: "mine", // 'mine' | 'team'
  setScope(s) { this.scope = s; },

  // -- overlays --
  searchOpen: false,
  notifOpen: false,
  shortcutsOpen: false,
  composer: null,     // { channel, mode, to, subject, ... } | null
  waWizard: null,     // { step, name, number } | null
  mergeContactId: null,
  insightsMemberId: null,

  openSearch() { this.searchOpen = true; },
  closeSearch() { this.searchOpen = false; },
  toggleNotif() { this.notifOpen = !this.notifOpen; },
  openShortcuts() { this.shortcutsOpen = true; },
  openComposer(cfg = {}) { this.composer = { channel: "email", mode: "new", ...cfg }; },
  closeComposer() { this.composer = null; },
  openWaWizard(cfg = {}) { this.waWizard = { step: 1, ...cfg }; },
  closeWaWizard() { this.waWizard = null; },
  openMerge(id) { this.mergeContactId = id; },
  closeMerge() { this.mergeContactId = null; },
  openInsightsMember(id) { this.insightsMemberId = id; },
  closeInsightsMember() { this.insightsMemberId = null; },

  // -- toasts --
  toasts: [],
  toast(text, icon = "pi-check-circle") {
    const id = ++_tid;
    this.toasts.push({ id, text, icon });
    setTimeout(() => { this.toasts = this.toasts.filter((t) => t.id !== id); }, 2600);
  },
});
